/**
 * DirectoryConsentCard — the member's own switch for being found by church
 * admins. Hidden entirely when they have no school or event record to share.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck } from 'lucide-react';

export const DirectoryConsentCard = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['my-directory-consent'],
    queryFn: async () => (await api.get('/member/directory-consent')).data,
  });

  const save = useMutation({
    mutationFn: async (granted) => (await api.put('/member/directory-consent',
      { granted })).data,
    onSuccess: (res) => {
      toast.success(res.shared
        ? 'Church admins can now find you when adding a member'
        : 'You are hidden from church directory searches again');
      qc.invalidateQueries({ queryKey: ['my-directory-consent'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!data || data.count === 0) return null;

  return (
    <Card data-testid="directory-consent-card">
      <CardContent className="p-4 flex flex-wrap items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Let church admins find me</p>
          <p className="text-xs text-slate-500 mt-0.5">
            You are on {data.count} {data.count === 1 ? 'roll' : 'rolls'} outside the church
            directory ({data.records.map((r) => r.unit_name || r.source_label).join(', ')}).
            Turn this on and a church admin adding an existing member can find your name, email
            and phone. Nothing else is shared, and you can turn it off whenever you like.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {data.records.map((r) => (
              <Badge key={r.record_id} variant="outline" className="text-[10px]">
                {r.source_label}{r.unit_name ? ` · ${r.unit_name}` : ''}
                {r.shared ? ' · shared' : ''}
              </Badge>
            ))}
          </div>
        </div>
        <Switch checked={!!data.shared} disabled={save.isPending}
                onCheckedChange={(v) => save.mutate(v)}
                data-testid="directory-consent-switch" />
      </CardContent>
    </Card>
  );
};

export default DirectoryConsentCard;
